import { FormikProps } from "formik";
import { ChangeEvent, useCallback, useState } from "react";
import { ReactComponent as UploadFileIcon } from "../../../assets/images/uploadFile.svg";
import { getCustomers } from "../../api/config";
import { useFetch } from "../../hooks/useFetch";
import { GetCustomersResponse } from "../../interfaces/be.interfaces";
import { McuFirmwareVersionInitValue, SelectOption } from "../../interfaces/fe.interfaces";
import Checkbox from "../Checkbox";
import Select from "../Select";
import Spinner from "../Spinner";
import styles from "./styles.module.scss";

/**
 * Common form component for Add or Edit Mcu Firmware version
 * Takes in all the props from Formik component
 */
const Form = ({
  values,
  handleChange,
  touched,
  errors,
  handleSubmit,
  setFieldValue,
  handleReset,
  handleBlur,
  isSubmitting,
  enableReinitialize,
  setFieldTouched,
}: FormikProps<McuFirmwareVersionInitValue>) => {
  const [customerList, setCustomerList] = useState<SelectOption<string | null>[]>([]);

  /**
   * Fetch list of customers to show in dropdown
   * only active customers are displayed in dropdown
   */
  const { loading: customerInfoLoading } = useFetch<GetCustomersResponse>({
    runOnMount: true,
    url: getCustomers,
    onSuccess: (response) => {
      // Prepare customer data in option format to show in dropdown and filter inactive customers
      const customerOptions: SelectOption<string>[] = response.data?.data?.customers
        ?.filter((item) => item.is_enable)
        .map((customer) => ({
          label: customer.customer_name,
          value: customer.vendor_id,
        }));

      setCustomerList(customerOptions);
    },
  });

  // Handle customer change
  const onChangeCustomer = useCallback((value: number) => {
    setFieldValue("vendor_id", value);
  }, []);

  const onMcuFirmwareBinChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFieldTouched("mcu_firmware_bin", true);
    setFieldValue("mcu_firmware_bin", event.target.files?.[0]);
  }, []);

  const onChangeVersion = useCallback((value: string) => {
    const regEx = /^[0-9.]+$/;

    // If value is empty or number or .
    if (regEx.test(value) || value === "") setFieldValue("firmware_version", value);
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.dialog}>
        {isSubmitting || customerInfoLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <input
                  id="firmware_version"
                  name="firmware_version"
                  type="text"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeVersion(e?.target?.value)}
                  placeholder="Add a firmware version"
                  value={values.firmware_version}
                />
                {errors?.firmware_version && touched.firmware_version ? <p className={styles.error}>{errors?.firmware_version}</p> : null}
              </div>
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <input
                  id="mcu_model"
                  name="mcu_model"
                  type="text"
                  onChange={handleChange}
                  placeholder="Add a mcu model"
                  value={values.mcu_model}
                />
                {errors?.mcu_model && touched.mcu_model ? <p className={styles.error}>{errors?.mcu_model}</p> : null}
              </div>
            </div>
            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <Select
                  selectProps={{
                    value: values.vendor_id,
                    name: "vendor_id",
                    placeholder: "Select Customer",
                    onBlur: handleBlur,
                    onChange: (event) => onChangeCustomer(+event.target.value),
                  }}
                  options={customerList}
                />
                {errors?.vendor_id && touched?.vendor_id ? <p className={styles.error}>{errors.vendor_id}</p> : null}
              </div>
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <textarea
                  name="firmware_description"
                  placeholder={"Add a firmware description"}
                  value={values.firmware_description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
                {errors?.firmware_description && touched.firmware_description ? (
                  <p className={styles.error}>{errors?.firmware_description}</p>
                ) : null}
              </div>
            </div>
            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <div className={styles.wisaFirmwareUploadFormGridItem}>
                  <p className={styles.wisaFirmwareUploadFormIconText}>Choose Firmware File</p>
                  <label className={styles.wisaFirmwareUploadFormIconLabel} htmlFor="mcu_firmware_bin">
                    <input id="mcu_firmware_bin" name="mcu_firmware_bin" type="file" onChange={onMcuFirmwareBinChange} hidden />
                    <UploadFileIcon className={styles.wisaFirmwareUploadFormUploadIcon} />
                    <div className={styles.wisaFirmwareUploadFormUploadMessagesWrapper}>
                      {values.mcu_firmware_bin?.name && (
                        <p className={styles.wisaFirmwareUploadFormFileName}>{values.mcu_firmware_bin?.name}</p>
                      )}
                      {touched.mcu_firmware_bin && errors.mcu_firmware_bin && (
                        <span className={styles.error}>{errors.mcu_firmware_bin}</span>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <Checkbox
                  label="MCU Firmware is current customer version?"
                  name="current_customer_version"
                  checked={values.current_customer_version}
                  disabled={enableReinitialize}
                  onChange={() => !enableReinitialize && setFieldValue("current_customer_version", !values.current_customer_version)}
                />
              </div>
            </div>

            <div className="w-100 d-flex justify-content-around">
              <button className={styles.dialogBtn} type="submit" disabled={isSubmitting}>
                {enableReinitialize ? "Update" : "Add"}
              </button>
              <button type="button" className={styles.dialogBtn} onClick={handleReset}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default Form;
